/**
 * Choosing the three starting Trajan tiles, and where each one goes.
 *
 * R-004: in player order, each player takes three tiles from the tops of the
 * six stacks, at most one per category, and places them on slots II, IV and VI
 * **in any order**. Where a tile goes is the decision: slot IV sits beside the
 * senate tray, and a tile there is satisfied by the markers in that tray — so
 * with the markers just arranged, the placement is what the arrangement was for.
 *
 * The first version took the tiles in the order they were clicked and put them
 * on II, IV and VI in that order without saying so. This is the placement made
 * explicit: pick a tile up, put it on a slot, move it to another if you change
 * your mind.
 */
import { MANIFESTS, type TrajanTilePick, type TrayIndex } from '@trajan/engine';

/** The slots a starting tile may go on, straight from the manifest (II, IV, VI). */
export const START_SLOTS: readonly TrayIndex[] = MANIFESTS.setup.initialTrajanTiles
  .slots as TrayIndex[];
const COUNT = MANIFESTS.setup.initialTrajanTiles.count;

export interface TilePlan {
  /** A stack whose top tile is in hand, waiting to be put on a slot. */
  holding: number | null;
  /** Slot → the stack its tile came from. */
  placed: Readonly<Record<number, number>>;
}

export const emptyPlan = (): TilePlan => ({ holding: null, placed: {} });

/** The slot a stack's tile is sitting on, if it has been placed. */
export const slotOf = (plan: TilePlan, stack: number): number | null => {
  for (const [slot, s] of Object.entries(plan.placed)) if (s === stack) return Number(slot);
  return null;
};

const without = (placed: TilePlan['placed'], slot: number) => {
  const next = { ...placed };
  delete next[slot];
  return next;
};

/**
 * A stack was chosen. Picking a placed tile lifts it back off its slot; picking
 * the one already in hand puts it down; picking any other takes it up instead.
 */
export function pickStack(plan: TilePlan, stack: number): TilePlan {
  const at = slotOf(plan, stack);
  if (at !== null) return { holding: stack, placed: without(plan.placed, at) };
  if (plan.holding === stack) return { ...plan, holding: null };
  return { ...plan, holding: stack };
}

/**
 * A slot was chosen. With a tile in hand it goes there — and a tile already
 * there comes back into hand, so tiles can be swapped between slots the way
 * markers are. With nothing in hand, a placed tile is lifted off.
 */
export function chooseSlot(plan: TilePlan, slot: number): TilePlan {
  if (!START_SLOTS.includes(slot as TrayIndex)) return plan;
  const there = plan.placed[slot];
  if (plan.holding === null) {
    return there === undefined ? plan : { holding: there, placed: without(plan.placed, slot) };
  }
  return {
    holding: there ?? null,
    placed: { ...plan.placed, [slot]: plan.holding },
  };
}

/** Every slot filled, so there is something to send. */
export const isComplete = (plan: TilePlan): boolean =>
  Object.keys(plan.placed).length === COUNT && plan.holding === null;

/** The command's payload, once the plan is complete. */
export function toPicks(plan: TilePlan): TrajanTilePick[] | null {
  if (!isComplete(plan)) return null;
  return Object.entries(plan.placed).map(([slot, stack]) => ({
    stack,
    slot: Number(slot) as TrayIndex,
  }));
}
